<template>
  <article class="panel p-bottom-4" data-cy="request-tags">
    <p
      class="panel-heading has-background-light has-text-weight-semibold is-size-4"
    >
      Tags
    </p>
    <div class="panel-content" v-if="!filteredRequestLoading">
      <div class="panel-section p-bottom-8">
        <div
          class="panel-item is-flex is-align-items-center is-justify-content-space-between"
          :class="{ 'panel-item--editable': canUpdateRequest }"
        >
          <p class="has-text-weight-semibold">Assigned tags</p>
          <NrTooltip label="Edit request tags" v-if="canUpdateRequest">
            <NrIconButton
              variant="minimal-neutral"
              :icon="{ name: 'pencil-alt', type: 'regular' }"
              aria-label="Edit request tags"
              @click="toggleModal(true)"
            />
          </NrTooltip>
        </div>
      </div>
      <div v-if="tags.length > 0">
        <NrTag
          class="m-right-8 m-top-8"
          v-for="(tag, index) in tags"
          :key="index"
          :label="tag.name"
          size="normal"
          tagColor="purple"
        />
      </div>
      <p v-else class="is-italic m-bottom-12">No tags assigned</p>
    </div>
    <NrLoadingAnimation v-else :count="3" />
    <NrModal
      title="Change tags"
      :show="showModal"
      :hasSecondaryButton="true"
      :disableButtons="updating"
      secondaryButtonText="Cancel"
      primaryButtonText="Save tags"
      @on-close-click="toggleModal(false)"
      @on-secondary-click="toggleModal(false)"
      @on-primary-click="handleSaveTags"
    >
      <form @submit.prevent.stop>
        <NrSelect
          :options="filteredTags"
          v-model="requestTags"
          placeholder="Search for tags"
          @search="handleTagSearch"
          :filterable="false"
          append-to-body
          multiple
          @load-more="handleTagScroll"
          :loading="dataFetchState.tags.fetching"
          :hasMore="!dataFetchState.tags.end"
          @open="handleTagSearch"
        >
          <template v-slot:label>Tags</template>
        </NrSelect>

        <p v-if="canUpdateRequest" class="help">
          Don't see a tag you need? Create one via the
          <a href="/admin/tags" class="has-text-weight-bold">tag page</a>.
        </p>
      </form>
    </NrModal>
  </article>
</template>

<script lang="ts">
import NrIconButton from '@nextrequestco/components/src/components/NrIconButton'
import NrModal from '@nextrequestco/components/src/components/NrModal'
import NrTag from '@nextrequestco/components/src/components/NrTag'
import NrTooltip from '@nextrequestco/components/src/components/NrTooltip'
import NrSelect from '@nextrequestco/components/src/components/NrSelect'
import NrLoadingAnimation from '@nextrequestco/components/src/components/NrLoadingAnimation'
import { debounce, throttle } from 'lodash'
import { mapActions, mapState } from 'vuex'

interface Tag {
  id: number
  name: string
}
export default {
  name: 'RequestTags',
  components: {
    NrIconButton,
    NrModal,
    NrTag,
    NrTooltip,
    NrSelect,
    NrLoadingAnimation
  },
  props: {
    requestId: {
      type: String
    },
    tags: {
      type: Array,
      default() {
        return []
      }
    }
  },
  data() {
    return {
      updating: false,
      showModal: false,
      requestTags: []
    }
  },
  mounted() {
    this.handleTagSearch = debounce(this.handleTagSearch, 400)
  },
  computed: {
    ...mapState('request_show', [
      'request',
      'dataFetchState',
      'dataOptions',
      'filteredRequestLoading'
    ]),

    canUpdateRequest(): boolean {
      return this.$ability.can('update', 'Request')
    },
    filteredTags() {
      return this.dataOptions.tags.map((tag: Tag) => ({
        label: tag.name,
        value: tag.id
      }))
    }
  },
  methods: {
    ...mapActions('request_show', [
      'updateRequest',
      'fetchRequestTags',
      'fetchTimeline',
      'fetchDataOptions'
    ]),

    handleTagScroll: throttle(function () {
      this.fetchDataOptions({
        searchText: this.dataFetchState.tags.search_text,
        dataType: 'tags'
      })
    }, 400),

    handleTagSearch: throttle(function (searchText = '') {
      this.fetchDataOptions({ searchText, dataType: 'tags' })
    }, 400),

    async handleSaveTags() {
      try {
        await this.updateRequest({
          request_id: this.requestId,
          tag_ids: this.requestTags.map(({ value }) => value)
        })
        await this.fetchRequestTags(this.requestId)
        await this.fetchTimeline({ pretty_id: this.requestId })
        this.$toasted.success('Successfully updated tags.')
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        this.$toasted.error(
          'There has been an error saving tags. Please refresh the page and try again.'
        )
      }
      this.updating = false
      this.toggleModal(false)
    },

    toggleModal(state?: boolean) {
      this.showModal = state ?? !this.showModal
      if (this.showModal) this.handleTagSearch()

      this.requestTags = this.tags.map((tag: Tag) => ({
        label: tag.name,
        value: tag.id
      }))
    }
  }
}
</script>
